import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../supabaseClient'; 

// Create the AuthContext
const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [emailUnverified, setEmailUnverified] = useState(false);
  const [userProducts, setUserProducts] = useState([]);
  const [plans, setPlans] = useState({ 
      chatbot: { active: false, plan: 'free' }, 
      anomaly: { active: false, plan: 'free' } 
  });

  // --- HELPER: Refresh Plans (Anomaly Logic) ---
  const refreshPlans = async () => {
    try {
      // Dynamic import to keep Anomaly logic working if service exists
      const p = await import('../services/subscriptionService').then(m => m.getUserPlans());
      setPlans(p);
    } catch (error) {
       // Silent fail if service is missing (safe for Chatbot-only mode)
       console.warn('Plans refresh skipped:', error.message);
    }
  };

  // --- HELPER: Refresh Products (Anomaly Logic) ---
  const refreshUserProducts = async (currentUserId) => {
    // Logic preserved: Anomaly uses user.id, but allows passing an ID
    const targetId = currentUserId || user?.id;
    
    if (!targetId) {
      setUserProducts([]);
      return;
    }

    try {
      const { data: products, error } = await supabase
        .from("user_products")
        .select("*")
        .eq("user_id", targetId)
        .eq("is_active", true);

      if (error) throw error;
      setUserProducts(products || []);
    } catch (error) {
      console.error('Failed to refresh user products:', error.message);
      setUserProducts([]);
    }
  };

  // --- AUTH LOGIC ---
  useEffect(() => {
    let mounted = true;

    async function loadSession() {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        
        if (mounted) {
            setSession(session);
            setUser(session?.user ?? null);
        }

        if (session?.user) {
          // 1. Check Email Verification (Anomaly Logic)
          const isUnverified = !session.user.email_confirmed_at;
          if (mounted) setEmailUnverified(isUnverified);

          // 2. Load Profile & Products (Anomaly Logic)
          const [profileRes] = await Promise.all([
            supabase.from("profiles").select("full_name, avatar_url").eq("id", session.user.id).single(),
            refreshUserProducts(session.user.id)
          ]);

          if (mounted) {
            if (profileRes.data) setProfile(profileRes.data);
            
            // 3. Load Plans
            await refreshPlans();
          }
        } else {
          // Reset state if no user
          if (mounted) {
              setProfile(null);
              setUserProducts([]);
              setPlans({ chatbot: { active: false, plan: 'free' }, anomaly: { active: false, plan: 'free' } });
              setEmailUnverified(false);
          }
        }
      } catch (error) {
        console.error("Auth Load Error:", error);
      } finally {
        if (mounted) setLoading(false);
      }
    }

    loadSession();

    // Listen for Auth Changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (mounted) {
          setSession(session);
          setUser(session?.user ?? null);
          setLoading(false);
          
          if (session?.user) {
              loadSession(); 
          } else {
              setProfile(null);
              setUserProducts([]);
              setPlans({ chatbot: { active: false, plan: 'free' }, anomaly: { active: false, plan: 'free' } });
          }
      }
    });

    return () => {
        mounted = false;
        subscription.unsubscribe();
    };
  }, []);

  // --- ACTIONS ---

  const logout = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setProfile(null);
    setUserProducts([]);
    window.location.href = '/'; 
  };

  const deleteAccount = async () => {
    try {
      // Try RPC call first (Anomaly requirement)
      const { error } = await supabase.rpc('delete_user_account');
      if (error) console.warn("RPC delete failed, falling back to simple logout");
      
      await logout();
      return true;
    } catch (err) {
      console.error('Delete Account failed', err);
      await logout();
      throw err;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        // --- Anomaly State ---
        user,
        session,
        profile,
        userProducts,
        plans,
        loading,
        emailUnverified,
        
        // --- Chatbot Requirement (The only addition) ---
        // We expose the token here so Chatbot API calls work
        token: session?.access_token ?? null, 

        // --- Actions ---
        logout,
        deleteAccount,
        refreshPlans,
        refreshUserProducts,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}